import "./lineChart.scss";
import React, { useState } from "react";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import GraphModal from "../graphModal/GraphModal";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterBadge from "../common/FilterBadge";
import Skeleton from "@mui/material/Skeleton";

const LineCharts = ({
  title = "Class Average",
  data = [],
  labels = [],
  loading,
  appliedFilters = [],
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const hasFilters = appliedFilters.length > 0 && !loading;

  // Transform API data to chart format
  const chartData = labels.map((label, index) => ({
    name: label,
    value: data[index] || 0,
  }));

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Find min and max values for YAxis domain
  const minValue = Math.min(...data) - 10;
  const maxValue = Math.max(...data) + 10;
  const yDomain = [0, 100];

  return (
    <div
      className="lineChartContainer"
      onMouseEnter={() => setShowFilters(true)}
      onMouseLeave={() => setShowFilters(false)}
    >
      <div className="chart-header">
        <h2 className="chartTitle">{title}</h2>
        {hasFilters && (
          <div className="filter-indicator">
            <FilterListIcon fontSize="small" />
          </div>
        )}
      </div>

      {hasFilters && showFilters && (
        <div className="filter-tooltip top-left">
          <div className="filter-header">Applied Filters:</div>
          <div className="filter-badges">
            {appliedFilters.map((filter, index) => (
              <FilterBadge
                key={index}
                label={filter.label}
                value={filter.value}
              />
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={400}
          animation="wave"
          className="chart-skeleton"
        />
      ) : (
        <>
          <div onClick={handleOpenModal} className="chart-wrapper">
            <ResponsiveContainer width="100%" height={400}>
              <RechartsLineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" padding={{ left: 30, right: 30 }} />
                <YAxis domain={yDomain} />
                <Tooltip
                  formatter={(value) => [`${value.toFixed(2)}%`, "Average"]}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#8884d8"
                  strokeWidth={2}
                  activeDot={{ r: 8 }}
                  name="Class Average"
                  label={({ x, y, value }) => (
                    <text
                      x={x}
                      y={y - 10}
                      fill="#8884d8"
                      fontSize={16}
                      fontWeight="bold"
                      textAnchor="middle"
                    >
                      {`${Math.round(value)}%`}
                    </text>
                  )}
                />
              </RechartsLineChart>
            </ResponsiveContainer>
          </div>

          <GraphModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title={title}
          >
            <ResponsiveContainer width="100%" height={500}>
              <RechartsLineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" padding={{ left: 30, right: 30 }} />
                <YAxis domain={yDomain} />
                <Tooltip
                  formatter={(value) => [`${value.toFixed(2)}%`, "Average"]}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#8884d8"
                  strokeWidth={2}
                  activeDot={{ r: 8 }}
                  name="Class Average"
                  label={({ x, y, value }) => (
                    <text
                      x={x}
                      y={y - 10}
                      fill="#8884d8"
                      fontSize={20}
                      fontWeight="bold"
                      textAnchor="middle"
                    >
                      {`${Math.round(value)}%`}
                    </text>
                  )}
                />
              </RechartsLineChart>
            </ResponsiveContainer>
          </GraphModal>
        </>
      )}
    </div>
  );
};

export default LineCharts;
